import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Button,
  Tooltip,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  MenuBook as PlaybookIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { toDate, formatDuration } from './SlaBadge';

const GENERAL_GROUP = '__general__';

const userLabel = (u) => {
  if (!u) return '';
  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return name || u.username || u.email || u._id;
};

/** ISO -> yyyy-mm-dd for <input type="date"> */
const toDateInput = (value) => {
  const d = toDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** yyyy-mm-dd -> ISO string at end of that local day, or null */
const fromDateInput = (value) => {
  if (!value) return null;
  const d = new Date(`${value}T23:59:59`);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const emptyForm = { title: '', assignee: '', dueAt: '' };

const AssigneeSelect = ({ value, onChange, users, disabled, label = 'Assignee' }) => (
  <FormControl size="small" sx={{ minWidth: 160 }} disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <MenuItem value=""><em>Unassigned</em></MenuItem>
      {users.map((u) => (
        <MenuItem key={u._id} value={u._id}>{userLabel(u)}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

/**
 * Checklist of case tasks with add / inline edit / toggle / delete.
 * Props: caseId, tasks, users, canEdit, onChange(tasks), onError(err, fallback), onNotice(msg)
 */
export const TaskList = ({ caseId, tasks = [], users = [], canEdit = false, onChange, onError, onNotice }) => {
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u._id, u])), [users]);
  const done = tasks.filter((t) => t.status === 'done').length;
  const total = tasks.length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const groups = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => {
      const key = t.playbook || GENERAL_GROUP;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    const ordered = [];
    if (map.has(GENERAL_GROUP)) ordered.push([GENERAL_GROUP, map.get(GENERAL_GROUP)]);
    map.forEach((list, key) => { if (key !== GENERAL_GROUP) ordered.push([key, list]); });
    return ordered;
  }, [tasks]);

  const run = async (fn, successMessage) => {
    try {
      setBusy(true);
      const res = await fn();
      if (res?.data?.tasks && onChange) onChange(res.data.tasks);
      if (successMessage && onNotice) onNotice(successMessage);
      return true;
    } catch (err) {
      if (onError) onError(err, 'Task update failed'); else console.error(err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    const title = form.title.trim();
    if (!title) return;
    const ok = await run(() => apiService.caseTasks.add(caseId, {
      title,
      assignee: form.assignee || null,
      dueAt: fromDateInput(form.dueAt),
    }), 'Task added');
    if (ok) setForm(emptyForm);
  };

  const handleToggle = (task) => run(() => apiService.caseTasks.update(caseId, task.id, {
    status: task.status === 'done' ? 'open' : 'done',
  }));

  const startEdit = (task) => {
    setEditingId(task.id);
    setEditForm({ title: task.title, assignee: task.assignee || '', dueAt: toDateInput(task.dueAt) });
  };

  const handleSaveEdit = async (task) => {
    const title = editForm.title.trim();
    if (!title) return;
    const ok = await run(() => apiService.caseTasks.update(caseId, task.id, {
      title,
      assignee: editForm.assignee || null,
      dueAt: fromDateInput(editForm.dueAt),
    }), 'Task updated');
    if (ok) setEditingId(null);
  };

  const handleDelete = (task) => {
    if (!window.confirm(`Remove task "${task.title}"?`)) return;
    run(() => apiService.caseTasks.remove(caseId, task.id), 'Task removed');
  };

  const dueInfo = (task) => {
    const due = toDate(task.dueAt);
    if (!due) return null;
    const overdue = task.status !== 'done' && due < new Date();
    return (
      <Typography component="span" variant="caption" color={overdue ? 'error.main' : 'text.secondary'}>
        {overdue ? `Overdue ${formatDuration(Date.now() - due.getTime())} · ` : 'Due '}
        {due.toLocaleDateString()}
      </Typography>
    );
  };

  const renderTask = (task) => {
    const isEditing = editingId === task.id;
    const isDone = task.status === 'done';
    const assignee = task.assignee ? usersById[task.assignee] : null;

    if (isEditing) {
      return (
        <ListItem key={task.id} alignItems="flex-start" sx={{ px: 0, flexWrap: 'wrap', gap: 1 }}>
          <TextField
            size="small"
            label="Title"
            value={editForm.title}
            onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveEdit(task); } }}
            sx={{ flex: '1 1 240px' }}
            disabled={busy}
            autoFocus
          />
          <AssigneeSelect value={editForm.assignee} onChange={(v) => setEditForm((f) => ({ ...f, assignee: v }))} users={users} disabled={busy} />
          <TextField
            size="small"
            type="date"
            label="Due"
            InputLabelProps={{ shrink: true }}
            value={editForm.dueAt}
            onChange={(e) => setEditForm((f) => ({ ...f, dueAt: e.target.value }))}
            disabled={busy}
          />
          <Box display="flex" gap={0.5}>
            <Tooltip title="Save"><span><IconButton size="small" color="primary" onClick={() => handleSaveEdit(task)} disabled={busy || !editForm.title.trim()}><SaveIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Cancel"><span><IconButton size="small" onClick={() => setEditingId(null)} disabled={busy}><CancelIcon fontSize="small" /></IconButton></span></Tooltip>
          </Box>
        </ListItem>
      );
    }

    return (
      <ListItem
        key={task.id}
        sx={{ px: 0 }}
        secondaryAction={canEdit ? (
          <Box display="flex" gap={0.5}>
            <Tooltip title="Edit"><span><IconButton size="small" onClick={() => startEdit(task)} disabled={busy}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Remove"><span><IconButton size="small" color="error" onClick={() => handleDelete(task)} disabled={busy}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
          </Box>
        ) : null}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Checkbox
            edge="start"
            size="small"
            checked={isDone}
            onChange={() => handleToggle(task)}
            disabled={!canEdit || busy}
            inputProps={{ 'aria-label': `Mark "${task.title}" ${isDone ? 'open' : 'done'}` }}
          />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" sx={{ textDecoration: isDone ? 'line-through' : 'none', color: isDone ? 'text.secondary' : 'text.primary' }}>
              {task.title}
            </Typography>
          }
          secondary={
            <Box component="span" display="flex" gap={1} flexWrap="wrap" alignItems="center">
              {task.description && <Typography component="span" variant="caption" color="text.secondary">{task.description}</Typography>}
              {assignee && <Chip component="span" size="small" variant="outlined" label={userLabel(assignee)} sx={{ height: 20, fontSize: 11 }} />}
              {dueInfo(task)}
              {isDone && task.completedAt && (
                <Typography component="span" variant="caption" color="text.secondary">
                  Completed {toDate(task.completedAt)?.toLocaleString()}
                  {task.completedBy && usersById[task.completedBy] ? ` by ${userLabel(usersById[task.completedBy])}` : ''}
                </Typography>
              )}
            </Box>
          }
          secondaryTypographyProps={{ component: 'div' }}
        />
      </ListItem>
    );
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 8, borderRadius: 4 }} color={total && done === total ? 'success' : 'primary'} />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {done}/{total} done{total ? ` (${pct}%)` : ''}
        </Typography>
      </Box>
      {busy && <LinearProgress sx={{ mb: 1 }} />}

      {total === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
          No tasks yet. Add one below or apply a playbook.
        </Typography>
      )}

      {groups.map(([key, list], idx) => (
        <Box key={key} mb={1}>
          {(key !== GENERAL_GROUP || groups.length > 1) && (
            <Box display="flex" alignItems="center" gap={1} mt={idx ? 1 : 0}>
              {key !== GENERAL_GROUP && <PlaybookIcon fontSize="small" color="action" />}
              <Typography variant="subtitle2" color="text.secondary">
                {key === GENERAL_GROUP ? 'General' : key}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {list.filter((t) => t.status === 'done').length}/{list.length}
              </Typography>
            </Box>
          )}
          <List dense disablePadding>
            {list.map(renderTask)}
          </List>
          {idx < groups.length - 1 && <Divider sx={{ mt: 1 }} />}
        </Box>
      ))}

      {canEdit && (
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mt={2}>
          <TextField
            size="small"
            label="New task"
            placeholder="What needs to be done?"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            sx={{ flex: '1 1 240px' }}
            disabled={busy}
          />
          <AssigneeSelect value={form.assignee} onChange={(v) => setForm((f) => ({ ...f, assignee: v }))} users={users} disabled={busy} />
          <TextField
            size="small"
            type="date"
            label="Due"
            InputLabelProps={{ shrink: true }}
            value={form.dueAt}
            onChange={(e) => setForm((f) => ({ ...f, dueAt: e.target.value }))}
            disabled={busy}
          />
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAdd} disabled={busy || !form.title.trim()}>
            Add
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TaskList;
